import { useState } from 'react';
import { Users, Search, ExternalLink, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SPECIALIZATIONS = ['heat_pumps', 'insulation', 'solar', 'ventilation', 'glazing', 'boilers', 'general'];

const STATUS_BADGE = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const CONFIDENCE_BADGE = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-red-100 text-red-700',
};

export default function InstallersTab({ installers, onApprove, onReject, onResearch }) {
  const [region, setRegion] = useState('');
  const [specialization, setSpecialization] = useState('heat_pumps');
  const [researching, setResearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const filtered = installers.filter(ins => {
    if (statusFilter && ins.status !== statusFilter) return false;
    return true;
  });

  const handleResearch = async () => {
    if (!region.trim()) { toast.error('Enter a region'); return; }
    setResearching(true);
    try {
      await onResearch(region.trim(), specialization);
      toast.success(`Installer research started for ${region}`);
    } catch { toast.error('Failed to start research'); }
    finally { setResearching(false); }
  };

  return (
    <div className="space-y-4" data-testid="installers-tab">
      {/* Research trigger */}
      <div className="bg-white rounded-xl border border-border p-4 shadow-sm">
        <h3 className="text-sm font-semibold mb-3">Research Installers</h3>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Region</label>
            <input type="text" value={region} onChange={e => setRegion(e.target.value)} placeholder="e.g. Noord-Holland"
              className="text-sm border border-border rounded-lg px-3 py-2 bg-white w-48" data-testid="installer-region-input" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Specialization</label>
            <select value={specialization} onChange={e => setSpecialization(e.target.value)}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-white" data-testid="installer-spec-select">
              {SPECIALIZATIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <button onClick={handleResearch} disabled={researching}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            data-testid="installer-research-btn">
            {researching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            Research
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-xs border border-border rounded-md px-2 py-1 bg-white" data-testid="installer-status-filter">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <span className="text-xs text-muted-foreground self-center">{filtered.length} installer(s)</span>
      </div>

      {/* Installer cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-8 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No installers yet. Use the research panel above to find local professionals.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(ins => {
            const sb = STATUS_BADGE[ins.status] || STATUS_BADGE.draft;
            const cb = CONFIDENCE_BADGE[ins.confidence] || CONFIDENCE_BADGE.low;
            const expanded = expandedId === ins.installer_id;
            return (
              <div key={ins.installer_id} className="bg-white rounded-xl border border-border shadow-sm overflow-hidden" data-testid={`installer-${ins.installer_id}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : ins.installer_id)}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-sm">{ins.company_name}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {ins.city}, {ins.region} · {ins.specializations?.map(s => s.replace(/_/g, ' ')).join(', ')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {ins.review_score ? `${ins.review_score}/5` : 'no score'} {ins.review_count ? `(${ins.review_count} reviews)` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${cb}`}>{ins.confidence}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-border p-4 bg-muted/30 space-y-3">
                    <div>
                      <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Source Evidence</h5>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Source:</span>
                        <span className="font-medium">{ins.source_name || 'Not provided'}</span>
                        {ins.source_url && (
                          <a href={ins.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-0.5">
                            Verify <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      {ins.website && (
                        <a href={ins.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 block">{ins.website}</a>
                      )}
                      {ins.notes && <p className="text-xs text-muted-foreground mt-1 italic">{ins.notes}</p>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <h5 className="text-xs font-semibold text-green-700 mb-1">Strengths</h5>
                        <ul className="space-y-0.5">{ins.strengths?.map((s, i) => <li key={i} className="text-xs text-green-700">+ {s}</li>)}</ul>
                      </div>
                      {ins.red_flags?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-red-700 mb-1">Red Flags</h5>
                          <ul className="space-y-0.5">{ins.red_flags.map((f, i) => <li key={i} className="text-xs text-red-700">- {f}</li>)}</ul>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2 border-t border-border">
                      {ins.status === 'draft' && (
                        <>
                          <button onClick={() => onApprove(ins.installer_id)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1" data-testid={`approve-installer-${ins.installer_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Approve
                          </button>
                          <button onClick={() => onReject(ins.installer_id)} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
